import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, ActivityIcon, ShieldCheckIcon, AlertTriangleIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReviewsGallery } from '../components/ReviewsGallery';
import { EnrollmentOffer } from '../components/EnrollmentOffer';
import { FAQSection } from '../components/FAQSection';
export function Course5Page() {
  const modules = [{
    id: '01',
    title: 'De ce apar comportamentele dificile',
    icon: <ActivityIcon size={48} className="text-brand-gold" data-id="element-843" />,
    bullets: ['Cele 4 funcții ale comportamentului: de ce face copilul tău crize', 'Cum să analizezi o criză ca un detectiv, nu ca un judecător', 'Diferența dintre un comportament învățat și o nevoie neîmplinită']
  }, {
    id: '02',
    title: 'Prevenția: Cum să oprești crizele înainte să înceapă',
    icon: <ShieldCheckIcon size={48} className="text-brand-gold" data-id="element-844" />,
    bullets: ['Rolul rutinelor vizuale și cum să le creezi acasă', 'Cum să faci tranzițiile mai ușoare (de la parc la casă, de la joacă la somn)', 'Cum să oferi alegeri limitate pentru a evita luptele de putere']
  }, {
    id: '03',
    title: 'Intervenția: Ce faci când ești în mijlocul furtunii',
    icon: <AlertTriangleIcon size={48} className="text-brand-gold" data-id="element-845" />,
    bullets: ['Cum să rămâi calmă când copilul tău urlă în mijlocul magazinului', 'Tehnici de de-escaladare care funcționează la copiii mici', 'De ce "time-out" nu funcționează și ce să folosești în schimb (time-in)']
  }, {
    id: '04',
    title: 'Autonomia: Cum să-l înveți să facă lucruri singur',
    icon: <StarIcon size={48} className="text-brand-gold" data-id="element-846" />,
    bullets: ['Etapele dezvoltării autonomiei: ce poate face un copil la 1, 2, 3 și 4 ani', 'Cum să-l înveți să se îmbrace, să mănânce singur și să strângă jucăriile', 'Cum să folosești metoda "pașilor mici" pentru a construi independența']
  }];
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="pt-[64px] md:pt-[72px]" data-id="element-847">
      {/* SECȚIUNEA 1 — HERO OFERTĂ */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-848">
        <div className="container-custom" data-id="element-849">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-850">
            <div className="max-w-xl" data-id="element-851">
              <h1 className="font-playfair text-[48px] md:text-[64px] font-bold text-white mb-6 leading-[1.1]" data-id="element-852">
                Comportament și Autonomie
              </h1>
              <p className="font-source text-[20px] md:text-[24px] text-brand-gold mb-10 italic" data-id="element-853">
                Înțelege de ce face ce face și cum să-l ajuți să devină
                independent, fără lupte de putere și fără pedepse.
              </p>
              <a href="#inscriere" className="btn-inverted text-[18px] px-8 py-4" data-id="element-854">
                Click aici pentru înscriere!
              </a>
            </div>
            <div className="relative rounded-[24px] overflow-hidden shadow-2xl border-4 border-white/10" data-id="element-855">
              <img src="https://assets.cdn.filesafe.space/3SQqBp51ZHIpTGzJ3Vu1/media/6a2bf333e5084c4b71a588ff.jpg" alt="Comportament și Autonomie" className="w-full h-full object-cover aspect-[4/3]" data-id="element-856" />
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 2 — DESCRIERE */}
      <section className="bg-brand-beige section-padding" data-id="element-857">
        <div className="container-custom" data-id="element-858">
          <div className="max-w-3xl mx-auto" data-id="element-859">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy mb-8 text-center" data-id="element-860">
              Ești epuizată de crizele zilnice și luptele de putere?
            </h2>
            <div className="font-source text-[16px] md:text-[18px] text-brand-text space-y-6" data-id="element-861">
              <p data-id="element-862">
                Fiecare zi pare un câmp de luptă. Nu vrea să se îmbrace, nu vrea
                să mănânce, face o criză uriașă când trebuie să plecați din
                parc. Ai încercat să-i explici frumos, ai încercat să-l ignori,
                poate chiar ai țipat din disperare, dar nimic nu pare să
                funcționeze pe termen lung. Te simți judecată de cei din jur și,
                mai rău, te simți vinovată la sfârșitul zilei.
              </p>
              <p data-id="element-863">
                Când nu înțelegi de ce copilul tău reacționează așa, tinzi să
                iei lucrurile personal. Crezi că o face intenționat ca să te
                supere sau că ești tu o mamă rea care nu știe să-l disciplineze.
                Dar adevărul este că, la vârste mici, comportamentul este doar o
                formă de comunicare a unei nevoi sau a unei frustrări pe care el
                nu știe să o exprime altfel.
              </p>
              <div className="bg-white p-6 rounded-[16px] border-l-4 border-brand-coral" data-id="element-864">
                <p className="font-medium text-brand-navy" data-id="element-865">
                  Cursul "C - Comportament și Autonomie" te învață să descifrezi
                  mesajul din spatele comportamentului. Vei învăța cum să previi
                  crizele înainte să înceapă, cum să gestionezi momentele
                  tensionate cu calm și cum să-ți ajuți copilul să devină
                  independent și cooperant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 3 — CE VEI DESCOPERI */}
      <section className="bg-brand-ivory section-padding" data-id="element-866">
        <div className="container-custom" data-id="element-867">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-16" data-id="element-868">
            Ce vei descoperi concret în Cursul 5: Comportament și Autonomie
          </h2>
          <div className="max-w-4xl mx-auto space-y-8" data-id="element-869">
            {modules.map(mod => <div key={mod.id} className="flex flex-col md:flex-row bg-white rounded-[24px] shadow-sm border border-gray-100 overflow-hidden p-8 gap-8 items-start" data-id="element-870">
                {/* Icon Side */}
                <div className="flex-shrink-0 w-24 h-24 rounded-full bg-brand-beige flex items-center justify-center" data-id="element-871">
                  {mod.icon}
                </div>

                {/* Content Side */}
                <div className="flex-grow" data-id="element-872">
                  <h3 className="font-playfair text-[24px] md:text-[28px] font-bold text-brand-navy mb-6 leading-[1.2]" data-id="element-873">
                    {mod.title}
                  </h3>
                  <ul className="space-y-4 flex flex-col" data-id="element-874">
                    {mod.bullets.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-875">
                        <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-1" size={20} data-id="element-876" />
                        <span className="font-source text-[15px] text-brand-text" data-id="element-877">
                          {item}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 4 — BENEFICII */}
      <section className="bg-brand-beige section-padding" data-id="element-878">
        <div className="container-custom" data-id="element-879">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-880">
            Ce se schimbă după ce parcurgi acest curs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-881">
            {[{
            title: 'Înțelegi exact de ce face crize',
            desc: 'Nu te mai simți neputincioasă. Știi exact care e cauza comportamentului și cum să răspunzi la ea, nu doar la simptom.'
          }, {
            title: 'Scazi numărul crizelor zilnice',
            desc: 'Prin aplicarea tehnicilor de prevenție și a rutinelor clare, vei observa o scădere dramatică a luptelor de putere din casa voastră.'
          }, {
            title: 'Știi cum să reacționezi la cald',
            desc: 'Nu mai țipi și nu te mai simți vinovată după. Ai un plan clar de acțiune pentru momentele în care copilul tău pierde controlul.'
          }, {
            title: 'Crești un copil independent',
            desc: 'Îl ajuți să devină capabil și încrezător, învățându-l să facă lucruri singur, adaptat vârstei lui.'
          }].map((card, i) => <div key={i} className="bg-white p-6 rounded-[16px] shadow-sm border-t-4 border-brand-gold" data-id="element-882">
                <h3 className="font-montserrat font-bold text-[16px] text-brand-navy mb-3" data-id="element-883">
                  {card.title}
                </h3>
                <p className="font-source text-[14px] text-brand-text" data-id="element-884">
                  {card.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 5 — CINE SUNT EU */}
      <section className="bg-brand-ivory section-padding" data-id="element-885">
        <div className="container-custom" data-id="element-886">
          <div className="max-w-4xl mx-auto" data-id="element-887">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-10" data-id="element-888">
              Cine sunt eu și de ce am creat acest curs
            </h2>
            <div className="flex flex-col-reverse md:grid md:grid-cols-3 gap-8 items-stretch" data-id="element-889">
              <div className="md:col-span-2 font-source text-[16px] md:text-[18px] text-brand-text space-y-4 text-center md:text-left flex flex-col justify-center" data-id="element-890">
                <p data-id="element-891">
                  Sunt Cristina Geană, psiholog clinician cu peste 11 ani de
                  experiență directă cu copii mici. Am văzut sute de părinți
                  epuizați de crizele de tantrum și de luptele de putere
                  zilnice.
                </p>
                <p data-id="element-892">
                  Am creat acest curs pentru a-ți arăta că există o cale de
                  mijloc între a fi un părinte autoritar și a-i permite
                  copilului să facă orice. O cale bazată pe înțelegere, limite
                  clare și conectare.
                </p>
              </div>
              <div className="md:col-span-1" data-id="element-893">
                <img src="https://cdn.magicpatterns.com/uploads/wvUBHAy6VhqaRqNiVJPB6X/Cristina_poze_%285%29.jpg" alt="Cristina Geană" className="w-full h-full object-cover object-top rounded-[24px] shadow-card" data-id="element-894" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 6 — PENTRU CINE ESTE / NU ESTE */}
      <section className="bg-brand-navy section-padding text-white" data-id="element-895">
        <div className="container-custom" data-id="element-896">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white text-center mb-12" data-id="element-897">
            Pentru cine este acest curs?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-898">
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-[24px] border border-white/20" data-id="element-899">
              <h3 className="font-playfair text-[28px] font-bold text-brand-gold mb-6 !text-brand-gold" data-id="element-900">
                Pentru cine este
              </h3>
              <ul className="space-y-4" data-id="element-901">
                {['Mame care se confruntă zilnic cu crize de tantrum și lupte de putere', 'Mame care simt că au încercat totul (pedepse, recompense, explicații) și nimic nu funcționează', 'Mame care vor să-și ajute copilul să devină mai independent'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-902">
                    <CheckCircle2Icon className="text-brand-gold flex-shrink-0 mt-0.5" size={20} data-id="element-903" />
                    <span className="font-source text-[15px] text-white/90" data-id="element-904">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-white/5 p-8 rounded-[24px] border border-white/10" data-id="element-905">
              <h3 className="font-playfair text-[28px] font-bold text-white mb-6 !text-white" data-id="element-906">
                Pentru cine NU este
              </h3>
              <ul className="space-y-4" data-id="element-907">
                {['Mame care caută metode de dresaj sau pedepse rapide', 'Mame care nu sunt dispuse să-și schimbe propriul comportament și reacțiile', 'Profesioniști care caută un curs de specialitate. Acest curs e pentru mame, în limbaj accesibil.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-908">
                    <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-909">
                      <span className="w-1.5 h-1.5 rounded-full bg-white/60" data-id="element-910"></span>
                    </div>
                    <span className="font-source text-[15px] text-white/70" data-id="element-911">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SECȚIUNEA 7 — OFERTĂ */}
      <EnrollmentOffer title="Ce primești și care este investiția ta" includes={['Acces pe viață la Modulul 5 - Comportament și Autonomie', 'Materiale video structurate pas cu pas', 'Tehnici practice de aplicat acasă', 'Informație clară, fără termeni medicali']} price="395 lei" note="Acest modul face parte din Colecția Completă Uite cum C.R.E.S.C., dar poate fi achiziționat și separat." data-id="element-912" />

      {/* SECȚIUNEA 8 — TESTIMONIALE */}
      <section className="bg-brand-ivory section-padding" data-id="element-913">
        <div className="container-custom" data-id="element-914">
          <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-brand-navy text-center mb-12" data-id="element-915">
            Ce spun mamele care deja au lucrat cu mine
          </h2>
          <ReviewsGallery data-id="element-916" />
        </div>
      </section>

      {/* SECȚIUNEA 9 — FAQ */}
      <FAQSection faqs={[{
      q: 'Când primesc acces la curs?',
      a: 'Imediat după confirmarea plății, vei primi pe email datele de acces la platforma unde se află cursul.'
    }, {
      q: 'Cât timp am acces la materiale?',
      a: 'Ai acces pe viață la acest modul. Îl poți revedea ori de câte ori ai nevoie, în ritmul tău.'
    }, {
      q: 'Pot cumpăra și restul modulelor mai târziu?',
      a: 'Da, poți achiziționa celelalte module separat sau poți opta pentru Colecția Completă C.R.E.S.C. pentru a beneficia de prețul special și de apelurile de grup.'
    }]} data-id="element-917" />

      {/* SECȚIUNEA 10 — ÎNSCRIERE */}
      <section id="inscriere" className="bg-brand-navy section-padding text-center text-white" data-id="element-918">
        <div className="container-custom" data-id="element-919">
          <div className="max-w-3xl mx-auto" data-id="element-920">
            <h2 className="font-playfair text-[32px] md:text-[40px] font-bold text-white mb-6" data-id="element-921">
              Înscriere Modulul 5 - Comportament și Autonomie
            </h2>
            <p className="font-source text-[18px] text-white/80 mb-8" data-id="element-922">
              Completează formularul de mai jos pentru a finaliza înscrierea.
            </p>

            <div className="bg-white rounded-[24px] overflow-hidden min-h-[600px]" data-id="element-923">
              <iframe src="https://api.leadconnectorhq.com/widget/form/RHkF6aWNtdZMNovgHhzT" style={{
              width: '100%',
              height: '100%',
              minHeight: '600px',
              border: 'none'
            }} id="inline-RHkF6aWNtdZMNovgHhzT" data-layout="{'id':'INLINE'}" data-trigger-type="alwaysShow" data-trigger-value="" data-activation-type="alwaysActivated" data-activation-value="" data-deactivation-type="neverDeactivate" data-deactivation-value="" data-form-name="Form Modul 5" data-height="600" data-layout-iframe-id="inline-RHkF6aWNtdZMNovgHhzT" data-form-id="RHkF6aWNtdZMNovgHhzT" title="Form Modul 5" data-id="element-924" />
            </div>
          </div>
        </div>
      </section>
    </motion.div>;
}